import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EnhancedMarkdown } from '../EnhancedMarkdown';
/** Render proposed content without writing to the user's document. */ export function DocumentProposalPreview({ operations }) {
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Proposed document content",
        className: "h-full overflow-auto p-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-5 rounded-lg border border-border bg-muted p-3 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("strong", {
                        children: "Draft preview — not yet applied"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "Review the proposed content here, then accept or reject it in chat. Layout and calculated spreadsheet values appear in the editor after acceptance."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-5 rounded-lg border border-border bg-background p-6 text-foreground",
                children: operations.map(({ action, params }, index)=>{
                    const text = params.content ?? params.markdown ?? params.text;
                    if (action === 'insert_markdown') {
                        return /*#__PURE__*/ _jsx(EnhancedMarkdown, {
                            content: String(text ?? '')
                        }, index);
                    }
                    if (action === 'insert_content' && Array.isArray(params.blocks)) {
                        return /*#__PURE__*/ _jsx("div", {
                            className: "space-y-3",
                            children: params.blocks.map((block, i)=>{
                                const content = block.text ?? block.runs?.map((run)=>run.text ?? '').join('') ?? '';
                                if (block.type === 'horizontal_rule') return /*#__PURE__*/ _jsx("hr", {}, i);
                                if (block.type === 'table' && block.rows) return /*#__PURE__*/ _jsx("table", {
                                    className: "w-full border-collapse text-sm",
                                    children: /*#__PURE__*/ _jsx("tbody", {
                                        children: block.rows.map((row, r)=>/*#__PURE__*/ _jsx("tr", {
                                                children: row.map((cell, c)=>/*#__PURE__*/ _jsx("td", {
                                                        className: "border border-border p-2",
                                                        children: typeof cell === 'string' ? cell : JSON.stringify(cell)
                                                    }, c))
                                            }, r))
                                    })
                                }, i);
                                return block.type === 'heading' ? /*#__PURE__*/ _jsx("h2", {
                                    className: "text-lg font-semibold",
                                    children: content
                                }, i) : /*#__PURE__*/ _jsx("p", {
                                    className: "whitespace-pre-wrap",
                                    children: content
                                }, i);
                            })
                        }, index);
                    }
                    if (action === 'replace_text') {
                        return /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("del", {
                                    className: "block text-muted-foreground",
                                    children: String(params.find ?? '')
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: String(params.replace ?? '')
                                })
                            ]
                        }, index);
                    }
                    if (action === 'set_cell_value' || action === 'set_formula' || action === 'add_table') {
                        const rows = params.values ?? params.rows ?? [
                            [
                                params.formula ?? params.value ?? ''
                            ]
                        ];
                        return /*#__PURE__*/ _jsxs("div", {
                            className: "overflow-x-auto",
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "mb-1 text-xs text-muted-foreground",
                                    children: [
                                        params.sheet,
                                        " ",
                                        params.range
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("table", {
                                    className: "w-full border-collapse text-sm",
                                    children: /*#__PURE__*/ _jsx("tbody", {
                                        children: (Array.isArray(rows) ? rows : [
                                            [
                                                rows
                                            ]
                                        ]).map((row, r)=>/*#__PURE__*/ _jsx("tr", {
                                                children: (Array.isArray(row) ? row : [
                                                    row
                                                ]).map((cell, c)=>/*#__PURE__*/ _jsx("td", {
                                                        className: "border border-border p-2 align-top",
                                                        children: String(cell ?? '')
                                                    }, c))
                                            }, r))
                                    })
                                })
                            ]
                        }, index);
                    }
                    if (action === 'add_slide' || action === 'add_text_box' || action === 'add_bullets' || action === 'insert_content' || action === 'add_shape') {
                        return /*#__PURE__*/ _jsxs("div", {
                            className: "whitespace-pre-wrap break-words",
                            children: [
                                params.title && /*#__PURE__*/ _jsx("h2", {
                                    className: "mb-2 text-lg font-semibold",
                                    children: String(params.title)
                                }),
                                text && /*#__PURE__*/ _jsx("p", {
                                    children: String(text)
                                }),
                                Array.isArray(params.lines) && /*#__PURE__*/ _jsx("ul", {
                                    className: "list-disc space-y-2 pl-5",
                                    children: params.lines.map((line, i)=>/*#__PURE__*/ _jsx("li", {
                                            children: String(line)
                                        }, i))
                                })
                            ]
                        }, index);
                    }
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "rounded border border-border p-3 text-sm",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "font-medium capitalize",
                                children: action.replace(/_/g, ' ')
                            }),
                            /*#__PURE__*/ _jsx("dl", {
                                className: "mt-2 space-y-1",
                                children: Object.entries(params).map(([name, value])=>/*#__PURE__*/ _jsxs("div", {
                                        className: "break-words",
                                        children: [
                                            /*#__PURE__*/ _jsxs("dt", {
                                                className: "inline font-medium capitalize",
                                                children: [
                                                    name.replace(/_/g, ' '),
                                                    ": "
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                className: "inline",
                                                children: typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')
                                            })
                                        ]
                                    }, name))
                            })
                        ]
                    }, index);
                })
            })
        ]
    });
}
